import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  AdminPanelSettings,
  Groups,
  ChevronLeft,
  ChevronRight,
  Article,
  People,
  Explore,
  BarChart,
  Palette,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'

export const SIDEBAR_WIDTH_FULL = 240
export const SIDEBAR_WIDTH_ICONS = 64
export const SIDEBAR_WIDTH = SIDEBAR_WIDTH_FULL // For backwards compatibility

// Sidebar collapse modes: 0 = hidden, 1 = icons only, 2 = full
export type SidebarCollapseMode = 0 | 1 | 2

interface NavItem {
  label: string
  path: string
  icon: React.ReactNode
  requiresAuth?: boolean
  requiresAdmin?: boolean
}

interface SidebarProps {
  mobileOpen?: boolean
  onDrawerToggle?: () => void
  isMobile?: boolean
  collapseMode?: SidebarCollapseMode
  onCollapseModeChange?: (mode: SidebarCollapseMode) => void
}

// Guest navigation items (not authenticated) - empty for admin portal
const guestNavItems: NavItem[] = []

// Authenticated user navigation items
const authenticatedNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/',
    icon: <AdminPanelSettings />,
    requiresAdmin: true,
  },
  // Divider after index 0 (Dashboard)
  {
    label: 'Users',
    path: '/users',
    icon: <People />,
    requiresAdmin: true,
  },
  {
    label: 'Groups',
    path: '/groups',
    icon: <Groups />,
    requiresAdmin: true,
  },
  {
    label: 'Adventures',
    path: '/adventures',
    icon: <Explore />,
    requiresAdmin: true,
  },
  {
    label: 'Themes',
    path: '/themes',
    icon: <Palette />,
    requiresAdmin: true,
  },
  // Divider after index 4 (Themes)
  {
    label: 'Statistics',
    path: '/statistics',
    icon: <BarChart />,
    requiresAdmin: true,
  },
  // Divider after index 4 (Statistics)
  {
    label: 'Logs',
    path: '/logs',
    icon: <Article />,
    requiresAdmin: true,
  },
]

export function Sidebar({
  mobileOpen = false,
  onDrawerToggle,
  isMobile = false,
  collapseMode = 2,
  onCollapseModeChange,
}: SidebarProps) {
  const location = useLocation()
  const { isAuthenticated, user } = useAuthStore()
  const isAdmin = user?.roles?.includes('Admin')

  const isIconsOnly = collapseMode === 1
  const isHidden = collapseMode === 0
  const currentWidth = isIconsOnly ? SIDEBAR_WIDTH_ICONS : SIDEBAR_WIDTH_FULL

  const handleCollapseToggle = () => {
    if (onCollapseModeChange) {
      // Cycle through modes: 2 -> 1 -> 0 -> 2
      const nextMode = collapseMode === 2 ? 1 : collapseMode === 1 ? 0 : 2
      onCollapseModeChange(nextMode as SidebarCollapseMode)
    }
  }

  // Choose navigation items based on authentication status
  const navItems = isAuthenticated ? authenticatedNavItems : guestNavItems

  // Filter admin items if needed
  const filteredNavItems = navItems.filter((item) => {
    if (item.requiresAdmin && !isAdmin) return false
    return true
  })

  // Separate main items from bottom items (Logs)
  const mainNavItems = filteredNavItems.filter((item) => item.label !== 'Logs')
  const bottomNavItems = filteredNavItems.filter((item) => item.label === 'Logs')

  const renderNavItem = (item: NavItem, index: number, showDividerAfter: boolean) => {
    const isActive = location.pathname === item.path

    const buttonContent = (
      <ListItemButton
        component={RouterLink}
        to={item.path}
        selected={isActive}
        onClick={isMobile ? onDrawerToggle : undefined}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          justifyContent: isIconsOnly ? 'center' : 'flex-start',
          px: isIconsOnly ? 1.5 : 2,
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
          },
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: isActive ? 'primary.contrastText' : 'primary.main',
            minWidth: isIconsOnly ? 'auto' : 40,
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!isIconsOnly && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: isActive ? 600 : 400,
            }}
          />
        )}
      </ListItemButton>
    )

    return (
      <Box key={item.path}>
        <ListItem disablePadding>
          {isIconsOnly ? (
            <Tooltip title={item.label} placement="right" arrow>
              {buttonContent}
            </Tooltip>
          ) : (
            buttonContent
          )}
        </ListItem>
        {showDividerAfter && <Divider sx={{ my: 1 }} />}
      </Box>
    )
  }

  const drawerContent = (
    <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Main navigation items at top */}
      <List sx={{ px: isIconsOnly ? 0.5 : 1, pt: 2, flex: 1 }}>
        {mainNavItems.map((item, index) => {
          // Dividers after Dashboard (index 0), Themes (index 4)
          const isDividerAfter = index === 0 || index === 4
          return renderNavItem(item, index, isDividerAfter)
        })}
      </List>

      {/* Bottom navigation items (Logs) */}
      {bottomNavItems.length > 0 && (
        <List sx={{ px: isIconsOnly ? 0.5 : 1, pb: 2 }}>
          <Divider sx={{ mb: 1 }} />
          {bottomNavItems.map((item, index) => renderNavItem(item, index, false))}
        </List>
      )}
    </Box>
  )

  const drawerStyles = {
    width: currentWidth,
    flexShrink: 0,
    transition: 'width 0.2s ease-in-out',
    '& .MuiDrawer-paper': {
      width: currentWidth,
      height: '100%',
      boxSizing: 'border-box',
      borderRight: '2px solid',
      borderColor: 'primary.main',
      backgroundImage: `url('/assets/images/Navigation_Background.png')`,
      backgroundSize: '400px',
      backgroundRepeat: 'repeat',
      position: 'relative',
      transition: 'width 0.2s ease-in-out',
      overflowX: 'hidden',
      overflowY: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'primary.main',
        opacity: 0.25,
        zIndex: 0,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'background.paper',
        opacity: 0.8,
        zIndex: 0,
      },
    },
  }

  return (
    <Box
      component="nav"
      sx={{
        width: { md: isHidden ? 0 : currentWidth },
        flexShrink: { md: 0 },
        transition: 'width 0.2s ease-in-out',
        position: 'relative',
        height: '100%',
      }}
    >
      {/* Collapse Toggle Button - positioned outside drawer */}
      {!isMobile && onCollapseModeChange && (
        <Tooltip title={collapseMode === 2 ? 'Collapse to icons' : collapseMode === 1 ? 'Hide sidebar' : 'Expand sidebar'} placement="right">
          <IconButton
            onClick={handleCollapseToggle}
            size="small"
            sx={{
              display: { xs: 'none', md: 'flex' },
              position: 'absolute',
              top: 12,
              right: -12,
              zIndex: 1300,
              width: 24,
              height: 24,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              color: 'primary.main',
              boxShadow: 1,
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
              },
            }}
          >
            {collapseMode === 0 ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          ...drawerStyles,
          width: SIDEBAR_WIDTH_FULL, // Always full width on mobile
          '& .MuiDrawer-paper': {
            ...drawerStyles['& .MuiDrawer-paper'],
            width: SIDEBAR_WIDTH_FULL,
            top: { xs: 56, sm: 64 }, // Account for AppBar height
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      {!isHidden && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: currentWidth,
            height: '100%',
            flexShrink: 0,
            transition: 'width 0.2s ease-in-out',
            '& .MuiDrawer-paper': {
              ...drawerStyles['& .MuiDrawer-paper'],
              position: 'relative',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  )
}
